import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { TERRAIN, NODES } from '../config';
import { applyCloudShadow } from './clouds';
import { terrainHeight, terrainSlope } from './terrain';

const B_CELL = 48, B_OFF = 4096, B_STRIDE = 8192; // bush stream grid (mirrors the node-grid key packing)

const UP = new THREE.Vector3(0, 1, 0);

// Decorative bushes scattered all over dry land. Built the same way as the tree foliage: clusters of
// alpha-cut LEAF CARDS with outward-pointing normals, so each bush shades as one soft leafy volume
// (the "fluffy" look) instead of a smooth blob. One InstancedMesh; per-instance variety comes from
// independent width + height scale, a random rotation, and a green->yellow-green->olive tint via
// instanceColor. Placement is biased by a low-frequency "bushiness" field so bushes clump into
// patches with sparser gaps, plus a baseline so a few appear everywhere. A subtle vertex-shader wind
// sways the leaves. Pure decoration: no collider, no harvest.

const BUSH_COUNT = 4600; // forests pile up bushes via bushiness^2; ~3x more in the woods (per the design ask)
const BUSH_SEED = 20260618; // fixed so regenerate lays out the same bushes for the same terrain
const SCATTER_RADIUS = 980; // matches the island; bushes fan out to this distance from spawn
const SCATTER_INNER = 6;    // ring inner radius so bushes do not pile on the spawn point
const DRY_MARGIN = 7.0;     // stay OFF the beach sand (sand band is ~waterline..seaLevel+8)

// Per-instance size ranges: width is applied to X and Z together so the clump stays round in plan,
// height to Y. Wide + short reads as a low spreading shrub, narrow + tall as a thin upright one.
const WIDTH_MIN = 0.7, WIDTH_MAX = 1.9;
const HEIGHT_MIN = 0.6, HEIGHT_MAX = 1.7;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Smooth low-frequency value noise driving the BUSHINESS field, so bushes cluster into patches with
// scarce gaps. Independent of the terrain, so the patches are a stable feature (like forestDensity).
function fHash(ix: number, iz: number): number {
  let h = (ix * 374761393 + iz * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
function fNoise(x: number, z: number): number {
  const xi = Math.floor(x), zi = Math.floor(z);
  const fx = x - xi, fz = z - zi;
  const sx = fx * fx * (3 - 2 * fx), sz = fz * fz * (3 - 2 * fz);
  const a = fHash(xi, zi), b = fHash(xi + 1, zi), c = fHash(xi, zi + 1), d = fHash(xi + 1, zi + 1);
  return (a + (b - a) * sx) * (1 - sz) + (c + (d - c) * sx) * sz;
}
// Match the tree forest-density field (same scale + offsets as nodes.ts forestDensity) so bushes pile
// into the SAME woodland pockets as the trees, clustering thickly around the tree bases.
function bushiness(x: number, z: number): number {
  const s = 0.0034;
  return fNoise(x * s, z * s) * 0.7 + fNoise(x * s * 2.7 + 50, z * s * 2.7 - 30) * 0.3;
}

// A soft cluster of small leaf blobs with see-through gaps (alpha-cut at render). White-ish so the
// per-instance instanceColor tint sets the actual leaf colour. Denser + smaller blobs than the tree
// leaf so a bush reads as fine foliage up close.
function makeLeafTexture(): THREE.CanvasTexture {
  const size = 128;
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);
  const rng = mulberry32(13);
  for (let i = 0; i < 120; i++) {
    const a = rng() * Math.PI * 2;
    const r = Math.pow(rng(), 0.7) * 0.46 * size;
    const x = size / 2 + Math.cos(a) * r;
    const y = size / 2 + Math.sin(a) * r * 0.95;
    const rad = 4 + rng() * 9;
    const shade = 0.7 + rng() * 0.3; // near-white; instanceColor provides the hue
    const c = Math.floor(225 * shade);
    const g = ctx.createRadialGradient(x, y, 0, x, y, rad);
    g.addColorStop(0, `rgba(${c},${c},${c},1)`);
    g.addColorStop(0.6, `rgba(${c},${c},${c},0.92)`);
    g.addColorStop(1, `rgba(${c},${c},${c},0)`);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, rad, 0, Math.PI * 2);
    ctx.fill();
  }
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

const LEAF_TEX = makeLeafTexture();

// Scatter leaf cards over an ellipsoid lobe, overriding each card's normals to point OUTWARD from the
// lobe centre, so the cards shade as one soft round volume (the same trick the trees use).
function addLeafLobe(
  out: THREE.BufferGeometry[], cx: number, cy: number, cz: number,
  rh: number, rv: number, count: number, sizeBase: number, seed: number,
): void {
  const rng = mulberry32(seed);
  const nd = new THREE.Vector3();
  for (let i = 0; i < count; i++) {
    const t = (i + 0.5) / count;
    const phi = Math.acos(1 - 2 * t);
    const theta = i * 2.399963; // golden angle for an even spread
    let dx = Math.sin(phi) * Math.cos(theta) + (rng() - 0.5) * 0.4;
    let dy = Math.cos(phi) + (rng() - 0.5) * 0.4;
    let dz = Math.sin(phi) * Math.sin(theta) + (rng() - 0.5) * 0.4;
    const len = Math.hypot(dx, dy, dz) || 1;
    dx /= len; dy /= len; dz /= len;
    const s = sizeBase * (0.7 + rng() * 0.6);
    const card = new THREE.PlaneGeometry(s, s);
    card.rotateY(rng() * Math.PI * 2);
    card.rotateX((rng() - 0.5) * 0.8);
    card.translate(cx + dx * rh, cy + dy * rv, cz + dz * rh);
    nd.set(dx, dy + 0.4, dz).normalize(); // outward, biased up so the bush catches top light
    const norm = card.attributes.normal;
    for (let v = 0; v < norm.count; v++) norm.setXYZ(v, nd.x, nd.y, nd.z);
    norm.needsUpdate = true;
    out.push(card);
  }
}

// A low, full, slightly irregular bush: a main lobe plus two smaller offset lobes. Seated with its
// base near y=0 so position + scale sit it on the ground. ~1 unit tall at scale 1.
function makeBushCanopy(): THREE.BufferGeometry {
  const cards: THREE.BufferGeometry[] = [];
  addLeafLobe(cards, 0.0, 0.52, 0.0, 0.62, 0.5, 22, 0.5, 31);
  addLeafLobe(cards, 0.42, 0.4, 0.22, 0.42, 0.36, 12, 0.46, 47);
  addLeafLobe(cards, -0.38, 0.46, -0.26, 0.44, 0.4, 12, 0.46, 59);
  const m = mergeGeometries(cards);
  if (!m) throw new Error('bush canopy merge failed');
  m.computeBoundingSphere();
  for (const c of cards) c.dispose();
  return m;
}

// Display-ready bush for the /admin LOD tool (real canopy geometry + the in-game leaf material).
export function buildDisplayBush(): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    map: LEAF_TEX, alphaTest: 0.5, side: THREE.DoubleSide, roughness: 0.85, metalness: 0, color: 0x6f9e52,
  });
  g.add(new THREE.Mesh(makeBushCanopy(), mat));
  return g;
}

export class BushField {
  readonly group = new THREE.Group();
  private readonly mesh: THREE.InstancedMesh;
  private readonly uTime: { value: number };
  private rng = mulberry32(BUSH_SEED);
  private readonly tmp = new THREE.Matrix4();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpScale = new THREE.Vector3();
  private readonly tmpCol = new THREE.Color();
  // Per-bush data kept on the CPU so the streamer can re-upload only the nearby bushes each pass.
  private readonly bx = new Float32Array(BUSH_COUNT);
  private readonly bz = new Float32Array(BUSH_COUNT);
  private readonly by = new Float32Array(BUSH_COUNT);
  private readonly bw = new Float32Array(BUSH_COUNT);
  private readonly bh = new Float32Array(BUSH_COUNT);
  private readonly byaw = new Float32Array(BUSH_COUNT);
  private readonly bcol = new Float32Array(BUSH_COUNT * 3);
  private readonly grid = new Map<number, number[]>(); // bush indices by stream cell
  private streamCellX = NaN; private streamCellZ = NaN;

  constructor(uTime: { value: number }) {
    this.uTime = uTime;
    // Alpha-cut leaf material: the texture gives the leafy silhouette, instanceColor sets each bush's
    // hue. DoubleSide so cards read from any angle. A matching depth material keeps shadows leafy.
    const mat = new THREE.MeshStandardMaterial({
      map: LEAF_TEX, alphaTest: 0.5, side: THREE.DoubleSide, roughness: 0.85, metalness: 0,
    });
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = this.uTime;
      shader.vertexShader = 'uniform float uTime;\n' + shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        {
          vec3 ip = (instanceMatrix * vec4(transformed, 1.0)).xyz;
          float sway = sin(uTime * 1.1 + ip.x * 0.25 + ip.z * 0.2) * 0.06 * max(0.0, transformed.y - 0.25);
          transformed.x += sway;
          transformed.z += sway * 0.6;
        }`,
      );
    };
    mat.customProgramCacheKey = () => 'oxide-bush-leaf';
    applyCloudShadow(mat, 'bush'); // drifting cloud shadows on the bushes too

    const geom = makeBushCanopy();
    this.mesh = new THREE.InstancedMesh(geom, mat, BUSH_COUNT);
    this.mesh.name = 'bush';
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    // Alpha-cut (leafy) shadows instead of solid quads.
    this.mesh.customDepthMaterial = new THREE.MeshDepthMaterial({
      depthPacking: THREE.RGBADepthPacking, map: LEAF_TEX, alphaTest: 0.5,
    });
    this.group.add(this.mesh);
    this.fill(); // build once at construction with the same routine regenerate uses
  }

  // Re-scatter every bush onto the CURRENT terrainHeight (call after the land changes). Reproducible:
  // the rng is reseeded so the same terrain yields the same bushes.
  regenerate(): void {
    this.fill();
  }

  // Scatter every bush against the current terrain and STORE it (pos/size/yaw/colour) + index it into the
  // stream grid. Nothing is uploaded here -- the first stream pass draws only the nearby bushes. Shared by
  // the constructor and regenerate; reseeds the rng first so the layout is deterministic.
  private fill(): void {
    this.rng = mulberry32(BUSH_SEED);
    this.grid.clear();
    for (let i = 0; i < BUSH_COUNT; i++) {
      const [x, z] = this.scatter();
      this.bx[i] = x; this.bz[i] = z; this.by[i] = terrainHeight(x, z);
      this.bw[i] = WIDTH_MIN + this.rng() * (WIDTH_MAX - WIDTH_MIN);
      this.bh[i] = HEIGHT_MIN + this.rng() * (HEIGHT_MAX - HEIGHT_MIN);
      this.byaw[i] = this.rng() * Math.PI * 2;
      // Light, varied tint (the leaf texture is near-white): green -> yellow-green -> olive.
      this.tmpCol.setHSL(0.2 + this.rng() * 0.12, 0.35 + this.rng() * 0.28, 0.5 + this.rng() * 0.22);
      this.bcol[i * 3] = this.tmpCol.r; this.bcol[i * 3 + 1] = this.tmpCol.g; this.bcol[i * 3 + 2] = this.tmpCol.b;
      const key = (Math.floor(x / B_CELL) + B_OFF) * B_STRIDE + (Math.floor(z / B_CELL) + B_OFF);
      let bucket = this.grid.get(key); if (!bucket) { bucket = []; this.grid.set(key, bucket); }
      bucket.push(i);
    }
    this.mesh.count = 0; // nothing drawn until the first stream pass
    this.streamCellX = NaN; this.streamCellZ = NaN; // force a re-stream after a (re)fill
  }

  // Re-upload only the bushes within drawRadius of the camera into compacted slots (mirrors the tree streamer).
  private streamRebuild(camX: number, camZ: number): void {
    const R = NODES.drawRadius, r2 = R * R;
    const cx0 = Math.floor((camX - R) / B_CELL), cx1 = Math.floor((camX + R) / B_CELL);
    const cz0 = Math.floor((camZ - R) / B_CELL), cz1 = Math.floor((camZ + R) / B_CELL);
    let k = 0;
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const bucket = this.grid.get((cx + B_OFF) * B_STRIDE + (cz + B_OFF));
        if (!bucket) continue;
        for (let bi = 0; bi < bucket.length && k < BUSH_COUNT; bi++) {
          const i = bucket[bi];
          const dx = this.bx[i] - camX, dz = this.bz[i] - camZ;
          if (dx * dx + dz * dz > r2) continue;
          this.tmpPos.set(this.bx[i], this.by[i], this.bz[i]);
          this.tmpQuat.setFromAxisAngle(UP, this.byaw[i]);
          this.tmpScale.set(this.bw[i], this.bh[i], this.bw[i]);
          this.tmp.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
          this.mesh.setMatrixAt(k, this.tmp);
          this.tmpCol.setRGB(this.bcol[i * 3], this.bcol[i * 3 + 1], this.bcol[i * 3 + 2]);
          this.mesh.setColorAt(k, this.tmpCol);
          k++;
        }
      }
    }
    this.mesh.count = k;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.mesh.computeBoundingSphere();
  }

  // Re-stream when the camera crosses a stream cell (call each frame with the camera position).
  update(camX: number, camZ: number): void {
    const cellX = Math.floor(camX / NODES.streamCell), cellZ = Math.floor(camZ / NODES.streamCell);
    if (cellX !== this.streamCellX || cellZ !== this.streamCellZ) {
      this.streamCellX = cellX; this.streamCellZ = cellZ;
      this.streamRebuild(camX, camZ);
    }
  }

  // Scatter one bush on dry land, biased by the bushiness field so bushes form patches with scarce
  // gaps. A baseline keeps a few everywhere; dense patches win most. Falls back to the last on-land
  // candidate if 40 tries do not accept.
  private scatter(): [number, number] {
    let x = 0, z = 0, landX = 0, landZ = 0, gotLand = false;
    for (let tries = 0; tries < 40; tries++) {
      const angle = this.rng() * Math.PI * 2;
      const dist = SCATTER_INNER + this.rng() * (SCATTER_RADIUS - SCATTER_INNER);
      x = Math.cos(angle) * dist;
      z = Math.sin(angle) * dist;
      if (terrainHeight(x, z) <= TERRAIN.seaLevel + DRY_MARGIN) continue; // off the beach sand
      landX = x; landZ = z; gotLand = true;
      const b = bushiness(x, z);
      const accept = 0.06 + 0.94 * b * b; // contrasty: a few in the open, TONS in forests
      // Steep slopes: bushes grow RARE, not impossible. slope is rise/run (~0.58 = 30 deg, ~0.84 = 40 deg);
      // smoothstep the penalty from 1 on gentle ground toward a 0.1 floor on cliffs.
      const sl = terrainSlope(x, z, 1.2);
      const tS = Math.min(1, Math.max(0, (sl - 0.58) / (0.84 - 0.58)));
      const slopePenalty = 0.1 + 0.9 * (1 - tS * tS * (3 - 2 * tS));
      if (this.rng() < accept * slopePenalty) return [x, z];
    }
    return gotLand ? [landX, landZ] : [x, z];
  }
}
